<template>
  <div>
    <sidebar v-if="showSidebar">
      <event-cancelled-notice v-if="eventStatus.cancelled" />
      <host-notice v-else-if="eventStatus.currentUserIsHost" />
      <rsvped-notice v-else-if="attendeeHasRsvped" />
      <full-notice v-else-if="eventStatus.full" />
      <open-rsvp v-else />
    </sidebar>

    <!-- These modals need to live here, outside the floating-sidebar,
         instead of next to the buttons that trigger them, because the floating-sidebar
         has a z-index situation that interferes with proper modal operation. -->
    <confirm-your-email-modal v-if="awaitingEmailConfirmation" :email-address="action.email"
                              :email-sent-message="i18n.t('public.confirm_your_email_modal.sent_for_attendee')"
                              :warning-message="i18n.t('public.events.confirm_email_warning')" />

    <confirm-not-attending-modal v-if="showConfirmNotAttendingModal" />

    <teleport v-if="locationRevealed" to="#event-location">
      <div v-html="fullLocationHtml" />
    </teleport>

    <teleport v-if="webConferenceLinkRevealed" to="#event-web-conference-link">
      <div v-html="webConferenceLinkHtml" />
    </teleport>

    <toast v-if="toastErrorMessage" kind="error">
      <template #message-content>
        <p>{{ toastErrorMessage }}</p>
      </template>
    </toast>

    <template v-if="showFixedRsvpButton">
      <modal-with-rsvp-form v-if="showingActionFormModal" class="action-form-modal" @modal-closed="hideActionFormModal" />

      <div v-else class="bottom-fixed d-grid d-md-none p-3">
        <button type="button" class="btn btn-primary btn-lg btn-show-signature-form" @click="showActionFormModal">
          {{ i18n.t('events.show.rsvp_event') }}
        </button>
      </div>
    </template>

    <daisy-chain-manager v-if="showDaisyChain" />
  </div>
</template>

<script>
import ConfirmNotAttendingModal from './confirm-not-attending-modal'
import ConfirmYourEmailModal from '@/components/confirm-your-email-modal'
import DaisyChainManager from '../../daisy-chains/manager'
import EventCancelledNotice from './event-cancelled-notice'
import FullNotice from './full-notice'
import HostNotice from './host-notice'
import { mapGetters, mapMutations, mapState } from 'vuex'
import ModalWithRsvpForm from './modal-with-rsvp-form'
import OpenRsvp from './open-rsvp'
import Sidebar from '@/campaign-action-shared/sidebar'
import RsvpedNotice from './rsvped-notice'
import Toast from '@/components/toast'

export default {
  components: { ConfirmNotAttendingModal, ConfirmYourEmailModal, DaisyChainManager, EventCancelledNotice, FullNotice,
                HostNotice, ModalWithRsvpForm, OpenRsvp, RsvpedNotice, Sidebar, Toast },

  computed: {
    ...mapState(['action', 'awaitingEmailConfirmation', 'baseDaisyChainsPath', 'completingAction', 'eventStatus',
                 'fullLocationHtml', 'globalSubscriptionCheck', 'locationRevealed', 'showConfirmNotAttendingModal',
                 'showDaisyChain', 'showingActionFormModal', 'toastErrorMessage', 'tryOneClickFromCrm',
                 'webConferenceLinkRevealed', 'webConferenceLinkHtml']),
    ...mapGetters(['attendeeHasRsvped', 'showSidebarForScreenSize']),

    showFixedRsvpButton() {
      return !this.eventStatus.currentUserIsHost && !this.attendeeHasRsvped
    },

    showSidebar() {
      return this.showSidebarForScreenSize || this.attendeeHasRsvped || this.eventStatus.currentUserIsHost
    }
  },

  watch: {
    locationRevealed(revealed) {
      if (revealed) {
        document.getElementById('initial-event-location-display').remove()
      }
    }
  },

  created() {
    window.addEventListener('resize', this.updateWindowWidth)
  },

  mounted() {
    if (this.completingAction && !this.awaitingEmailConfirmation) {
      this.$store.dispatch('startDaisyChain', this.baseDaisyChainsPath)
    }

    if (this.tryOneClickFromCrm) {
      this.$store.dispatch('fetchOneClickDataFromCrm')
    } else if (this.globalSubscriptionCheck.enabled) {
      this.$store.dispatch('fetchGlobalSubscriptionDataFromCrm')
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateWindowWidth)
  },

  methods: {
    ...mapMutations(['hideActionFormModal', 'showActionFormModal']),

    updateWindowWidth() {
      this.$store.commit('setWindowWidth', window.innerWidth)
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~phoenix_styles/colors" as colors;

.bottom-fixed {
  background-color: #FFFFFF;
}

::v-deep(.action-form-modal .modal-content) {
  background: colors.$background-light-gray;
}
</style>
